import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';

// ─── Theme Context ──────────────────────────────────────────────────────────
//
// Provides a 3-state preference (`light` / `dark` / `system`) so users can
// explicitly opt in, opt out, or follow their OS. The resolved boolean
// `isDark` is what consumers usually need — it accounts for system changes
// in real time when preference === 'system'.
//
// Persistence: stored in localStorage under the `snapsule:theme` key.
// Default: 'system' so first-time visitors get whatever they're used to.
//
// We toggle the `dark` class on <html> ourselves (Tailwind is configured
// with `darkMode: 'class'`) and we also stamp the preference onto
// <html data-theme="..."> for any non-Tailwind CSS that wants to react.

const STORAGE_KEY = 'snapsule:theme';
const VALID_PREFS = new Set(['light', 'dark', 'system']);

const ThemeContext = createContext({
  preference: 'system',
  isDark: false,
  setPreference: () => {},
  toggle: () => {},
});

const readStoredPreference = () => {
  if (typeof window === 'undefined') return 'system';
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return VALID_PREFS.has(stored) ? stored : 'system';
  } catch (e) {
    return 'system';
  }
};

const readSystemPrefersDark = () => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  const mq = window.matchMedia('(prefers-color-scheme: dark)');
  return mq?.matches ?? false;
};

const applyHtmlClass = (preference) => {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.classList.add('dark');
  root.setAttribute('data-theme', 'dark');
  root.setAttribute('data-theme-preference', preference);
  // Hint the user-agent so native form controls / scrollbars also flip.
  root.style.colorScheme = 'dark';
};

export const ThemeProvider = ({ children }) => {
  const [preference, setPreferenceState] = useState(() => readStoredPreference());
  const [, setSystemPrefersDark] = useState(() => readSystemPrefersDark());

  // Resolve the effective theme.
  const isDark = true;

  // Listen for OS theme changes so users who picked 'system' get live
  // updates without a refresh.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => setSystemPrefersDark(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', handler);
    else mq.addListener(handler); // legacy Safari
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', handler);
      else mq.removeListener(handler);
    };
  }, []);

  // Apply the class + persist any explicit choice.
  useEffect(() => {
    applyHtmlClass(preference);
  }, [preference]);

  const setPreference = useCallback((next) => {
    if (!VALID_PREFS.has(next)) return;
    setPreferenceState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch (e) { /* localStorage disabled — that's fine, in-memory still works */ }
  }, []);

  // Two-state toggle: if the user is currently dark (regardless of source),
  // switch to explicit light; otherwise switch to explicit dark. This makes
  // the button feel deterministic.
  const toggle = useCallback(() => {
    setPreference(isDark ? 'light' : 'dark');
  }, [isDark, setPreference]);

  const value = useMemo(
    () => ({ preference, isDark, setPreference, toggle }),
    [preference, isDark, setPreference, toggle]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => useContext(ThemeContext);

export default ThemeContext;
